import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Bell,
  CheckCircle2,
  Clock,
  MessageSquare,
  Award,
  X,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { supabase } from '../../utils/supabase/client';
import { getStudentSession } from '../../utils/supabase/auth';
import { toast } from 'sonner';

type Notification = {
  id: string;
  type: 'grade' | 'comment' | 'achievement' | 'reminder';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
};

export default function StudentNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadNotifications = async () => {
      try {
        setLoading(true);
        setError(null);
        const studentSession = getStudentSession();
        if (!studentSession) {
          setNotifications([]);
          setError('Please join a session first');
          return;
        }

        const { data: submissions, error: submissionsError } = await supabase
          .from('submissions')
          .select(`
            id,
            answer,
            score,
            graded,
            created_at,
            updated_at,
            slides (
              id,
              content,
              lessons (
                id,
                title
              )
            )
          `)
          .eq('student_name', studentSession.studentName)
          .order('updated_at', { ascending: false })
          .limit(50);

        if (submissionsError) throw submissionsError;

        const { data: grades, error: gradesError } = await supabase
          .from('grades')
          .select(`
            id,
            lesson_id,
            total_score,
            max_possible_score,
            updated_at,
            lessons (
              id,
              title
            )
          `)
          .eq('student_name', studentSession.studentName)
          .order('updated_at', { ascending: false })
          .limit(20);

        if (gradesError) throw gradesError;

        const gradeNotifications: Notification[] = (submissions || [])
          .filter((submission: any) => submission.graded && submission.score !== null)
          .map((submission: any) => {
            const slideTitle =
              submission.slides?.content?.title ||
              submission.slides?.content?.question ||
              submission.slides?.content?.prompt ||
              'Activity';
            const lessonTitle = submission.slides?.lessons?.title || 'Lesson';
            return {
              id: `grade-${submission.id}`,
              type: 'grade',
              title: 'Activity graded',
              message: `${slideTitle} in ${lessonTitle} scored ${submission.score}.`,
              timestamp: new Date(submission.updated_at || submission.created_at),
              read: false,
              actionUrl: `/student/activity/${submission.id}`,
            };
          });

        const feedbackNotifications: Notification[] = (submissions || [])
          .filter((submission: any) => submission.answer?.feedback)
          .map((submission: any) => {
            const slideTitle =
              submission.slides?.content?.title ||
              submission.slides?.content?.question ||
              submission.slides?.content?.prompt ||
              'Activity';
            const lessonTitle = submission.slides?.lessons?.title || 'Lesson';
            return {
              id: `comment-${submission.id}`,
              type: 'comment',
              title: 'Teacher feedback',
              message: `Feedback posted for ${slideTitle} in ${lessonTitle}.`,
              timestamp: new Date(submission.updated_at || submission.created_at),
              read: false,
              actionUrl: `/student/activity/${submission.id}`,
            };
          });

        const gradeSummaryNotifications: Notification[] = (grades || []).map((grade: any) => {
          const maxScore = grade.max_possible_score || 0;
          const percentage = maxScore > 0 ? Math.round((grade.total_score / maxScore) * 100) : 0;
          return {
            id: `lesson-grade-${grade.id}`,
            type: 'grade',
            title: 'Lesson score updated',
            message: `${grade.lessons?.title || 'Lesson'} overall score: ${percentage}%.`,
            timestamp: new Date(grade.updated_at),
            read: false,
            actionUrl: `/student/lesson/${grade.lesson_id}`,
          };
        });

        const pendingCount = (submissions || []).filter((submission: any) => !submission.graded).length;
        const reminderNotifications: Notification[] = pendingCount > 0
          ? [
              {
                id: `reminder-${studentSession.studentName}`,
                type: 'reminder',
                title: 'Pending review',
                message: `You have ${pendingCount} activit${pendingCount === 1 ? 'y' : 'ies'} awaiting grading.`,
                timestamp: new Date(),
                read: false,
              },
            ]
          : [];

        const mergedNotifications = [
          ...gradeNotifications,
          ...feedbackNotifications,
          ...gradeSummaryNotifications,
          ...reminderNotifications,
        ].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

        if (!isMounted) return;

        setNotifications((prev) => {
          const readState = new Map(prev.map((item) => [item.id, item.read]));
          return mergedNotifications.map((item) => ({
            ...item,
            read: readState.get(item.id) ?? item.read,
          }));
        });
      } catch (err) {
        console.error('Error loading notifications:', err);
        setError('Failed to load notifications');
        toast.error('Failed to load notifications');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadNotifications();
    const intervalId = setInterval(loadNotifications, 20000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'grade':
        return <CheckCircle2 className="w-6 h-6 text-green-600" />;
      case 'comment':
        return <MessageSquare className="w-6 h-6 text-blue-600" />;
      case 'achievement':
        return <Award className="w-6 h-6 text-amber-600" />;
      case 'reminder':
        return <Clock className="w-6 h-6 text-orange-600" />;
    }
  };

  const getNotificationColor = (type: Notification['type']) => {
    switch (type) {
      case 'grade':
        return 'bg-green-100';
      case 'comment':
        return 'bg-blue-100';
      case 'achievement':
        return 'bg-amber-100';
      case 'reminder':
        return 'bg-orange-100';
    }
  };

  const markAsRead = (id: string) => {
    setNotifications(notifications.map(n =>
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = (id: string) => {
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const unreadCount = useMemo(
    () => notifications.filter(n => !n.read).length,
    [notifications]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">{error}</h2>
          <Button onClick={() => navigate('/student/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <Button
                variant="ghost"
                onClick={() => navigate('/student/dashboard')}
                className="rounded-xl text-xs sm:text-sm p-2"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-semibold text-foreground truncate">Notifications</h1>
                {unreadCount > 0 && (
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">
                    {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                onClick={markAllAsRead}
                className="rounded-xl text-xs sm:text-sm p-2 sm:px-4"
                size="sm"
              >
                <span className="hidden sm:inline">Mark all as read</span>
                <span className="sm:hidden">Read all</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {notifications.length === 0 ? (
          <div className="bg-card border border-border rounded-2xl p-8 sm:p-12 text-center">
            <Bell className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-3 sm:mb-4 opacity-50" />
            <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-1 sm:mb-2">No notifications</h3>
            <p className="text-xs sm:text-base text-muted-foreground">You are all caught up.</p>
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            <AnimatePresence>
              {notifications.map((notification) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  layout
                  className={`bg-card border border-border rounded-xl overflow-hidden transition-all ${
                    !notification.read ? 'ring-2 ring-primary/20' : ''
                  }`}
                >
                  <div
                    className="p-3 sm:p-6 cursor-pointer hover:bg-accent/5 transition-colors"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-2 sm:gap-4">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${getNotificationColor(notification.type)} flex items-center justify-center flex-shrink-0`}>
                        {getNotificationIcon(notification.type)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 sm:gap-3 mb-1">
                          <h3 className="font-semibold text-xs sm:text-base text-foreground truncate">{notification.title}</h3>
                          {!notification.read && (
                            <Badge className="bg-primary text-primary-foreground flex-shrink-0 text-xs">
                              New
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground mb-1">{notification.message}</p>
                        <p className="text-xs text-muted-foreground">
                          {notification.timestamp.toLocaleString()}
                        </p>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                        className="rounded-lg flex-shrink-0 p-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Quick Stats */}
        {notifications.length > 0 && (
          <div className="mt-6 sm:mt-8 bg-gradient-to-br from-primary/10 to-accent/10 border border-border rounded-2xl p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-muted-foreground">New Grades</p>
                  <p className="text-lg sm:text-xl font-bold text-foreground">
                    {notifications.filter(n => n.type === 'grade' && !n.read).length}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-muted-foreground">New Comments</p>
                  <p className="text-lg sm:text-xl font-bold text-foreground">
                    {notifications.filter(n => n.type === 'comment' && !n.read).length}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Award className="w-5 h-5 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-muted-foreground">Reminders</p>
                  <p className="text-lg sm:text-xl font-bold text-foreground">
                    {notifications.filter(n => n.type === 'reminder' && !n.read).length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
